import { Component } from '@theme/component';

/**
 * Announcement banner marquee custom element.
 * Continuously scrolls content horizontally with play/pause controls.
 *
 * @extends {Component}
 */
export class AnnouncementBar extends Component {
  /** Whether the user intends the marquee to be playing */
  #playing = true;

  /** Whether the mouse is currently hovering over the bar */
  #hovering = false;

  connectedCallback() {
    super.connectedCallback();

    // Respect user preference for reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#playing = false;
      this.#updateButton();
      return;
    }

    // Defer until layout is ready so we can measure real widths
    requestAnimationFrame(() => {
      this.#setupMarquee();
      this.#bindEvents();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * Fills the track with enough clones to cover the viewport seamlessly,
   * then sets --marquee-shift to the exact pixel width of one original set
   * so the animation loops without any gap or jump.
   */
  #setupMarquee() {
    const track = this.querySelector('.announcement-bar__track');
    if (!track) return;

    const originalItems = Array.from(track.children);
    if (originalItems.length === 0) return;

    const originalWidth = track.scrollWidth;
    if (originalWidth === 0) return;

    // Clone enough sets to always fill at least 2× the container width
    const containerWidth = this.offsetWidth || window.innerWidth;
    const copies = Math.max(2, Math.ceil((containerWidth * 2) / originalWidth) + 1);

    for (let i = 0; i < copies; i++) {
      originalItems.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
    }

    // Pixel-perfect: move exactly one original set width, then loop
    track.style.setProperty('--marquee-shift', `-${originalWidth}px`);

    // Start animation now that the track is ready
    this.#applyAnimationState(true);
  }

  #bindEvents() {
    const btn = this.querySelector('.announcement-bar__play-pause');
    if (btn) btn.addEventListener('click', this.#togglePlayPause);

    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  #togglePlayPause = () => {
    this.#playing = !this.#playing;
    this.#applyAnimationState(this.#playing && !this.#hovering);
    this.#updateButton();
  };

  #handleMouseEnter = () => {
    this.#hovering = true;
    this.#applyAnimationState(false);
  };

  #handleMouseLeave = () => {
    this.#hovering = false;
    if (this.#playing) this.#applyAnimationState(true);
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#applyAnimationState(false);
    } else if (this.#playing && !this.#hovering) {
      this.#applyAnimationState(true);
    }
  };

  #applyAnimationState(running) {
    const track = this.querySelector('.announcement-bar__track');
    if (track) track.style.animationPlayState = running ? 'running' : 'paused';
  }

  #updateButton() {
    const btn = this.querySelector('.announcement-bar__play-pause');
    if (!btn) return;

    const pauseIcon = btn.querySelector('.announcement-bar__pause-icon');
    const playIcon = btn.querySelector('.announcement-bar__play-icon');

    if (pauseIcon) pauseIcon.hidden = !this.#playing;
    if (playIcon) playIcon.hidden = this.#playing;

    btn.setAttribute('aria-label', this.#playing ? 'Pause' : 'Play');
    btn.setAttribute('aria-pressed', String(!this.#playing));
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
